import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import BusinessIcon from '@mui/icons-material/Business';

const Home: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ color: 'primary.main' }}>
          IdanBean
        </Typography>
        <Typography variant="h5" component="h2" gutterBottom align="center" color="text.secondary">
          企業級雲服務解決方案
        </Typography>
        
        {/* 主要服務展示 */}
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardMedia
                component="img"
                height="400"
                image="https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80"
                alt="雲基礎設施"
                sx={{
                  objectFit: 'cover',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(rgba(13, 71, 161, 0.3), rgba(13, 71, 161, 0.7))',
                  }
                }}
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="div" sx={{ color: 'primary.main' }}>
                  <CloudIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  雲基礎設施
                </Typography>
                <Typography variant="body1" paragraph>
                  提供高性能、可擴展的雲計算基礎設施，支持企業級應用部署和運營。我們的雲平台確保99.99%的可用性，為您的業務提供可靠保障。
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="高性能計算" secondary="強大的計算能力和資源彈性擴展" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="全球數據中心" secondary="多區域部署，確保低延遲訪問" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="智能資源調度" secondary="自動化的資源優化和成本控制" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardMedia
                component="img"
                height="400"
                image="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80"
                alt="企業解決方案"
                sx={{
                  objectFit: 'cover',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(rgba(0, 188, 212, 0.3), rgba(0, 188, 212, 0.7))',
                  }
                }}
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="div" sx={{ color: 'primary.main' }}>
                  <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  企業解決方案
                </Typography>
                <Typography variant="body1" paragraph>
                  為企業提供全方位的雲服務解決方案，包括混合雲部署、數據管理、安全合規等服務，助力企業數字化轉型。
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="混合雲架構" secondary="靈活的部署方案，滿足不同業務需求" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="數據管理" secondary="高效的數據存儲、備份和恢復方案" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="安全合規" secondary="符合國際標準的安全保障體系" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 核心優勢 */}
        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="h4" component="h3" gutterBottom align="center" sx={{ color: 'primary.main' }}>
            核心優勢
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <SpeedIcon sx={{ mr: 1 }} />
                    高性能
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    採用最新一代硬件架構，提供卓越的計算性能和網絡吞吐量。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ mr: 1 }} />
                    安全可靠
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    多層安全防護，數據加密存儲，確保業務數據安全。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <StorageIcon sx={{ mr: 1 }} />
                    彈性擴展
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    靈活的資源配置，支持業務快速擴展，按需付費。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ mr: 1 }} />
                    專業服務
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    7x24小時技術支持，專業的解決方案團隊，確保業務穩定運行。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* 行動召喚按鈕 */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large" 
            href="https://www.idanbean.io"
            sx={{ 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 2
            }}
          >
            了解更多
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Home; 